import { FC, ReactNode, useEffect, useState } from 'react';
import cx from 'classnames';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { groupNearby } from './groups';
import { scaleOf } from './scale';
import styles from './DotRange.module.scss';

export type DotRangePoint = {
  id: string;
  value: number;
  tooltip?: ReactNode;
};

type Props = {
  points: DotRangePoint[];
  marker?: number;
};

const MERGE_DISTANCE_PX = 12;

const useWidthOf = (element: HTMLElement | null) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return width;
};

const DotRange: FC<Props> = ({ points, marker }) => {
  const [inner, setInner] = useState<HTMLDivElement | null>(null);
  const width = useWidthOf(inner);
  const values = points.map(point => point.value);
  const position = scaleOf(marker === undefined ? values : [...values, marker]);
  const start = position(Math.min(...values));
  const end = position(Math.max(...values));
  const mergeDistance = width ? (MERGE_DISTANCE_PX / width) * 100 : 0;
  const groups = groupNearby(points.map(point => ({ ...point, position: position(point.value) })), mergeDistance);

  return (
    <div className={styles.track}>
      <div ref={setInner} className={styles.inner}>
        {points.length > 0 && <div className={styles.span} style={{ left: `${start}%`, width: `${end - start}%` }} />}
        {marker !== undefined && <div className={styles.marker} style={{ left: `${position(marker)}%` }} />}
        {groups.map(({ id, position: groupPosition, members }) => (
          <TooltipContainer
            key={id}
            renderReferenceComponent={(className, ref) => (
              <span
                ref={ref}
                className={cx(className, styles.dot, { [styles.merged]: members.length > 1 })}
                style={{ left: `${groupPosition}%` }}
              >
                {members.length > 1 && members.length}
              </span>
            )}
          >
            {members.length > 1 ? (
              <div className={styles.groupTooltip}>
                {members.map(member => <div key={member.id} className={styles.groupTooltipItem}>{member.tooltip}</div>)}
              </div>
            ) : members[0].tooltip}
          </TooltipContainer>
        ))}
      </div>
    </div>
  );
};

type LegendProps = {
  pointLabel: string;
  spanLabel: string;
  markerLabel: string;
};

export const DotRangeLegend: FC<LegendProps> = ({ pointLabel, spanLabel, markerLabel }) => (
  <span className={styles.legend}>
    <span className={styles.legendItem}><span className={styles.legendDot} />{pointLabel}</span>
    <span className={styles.legendItem}><span className={styles.legendSpan} />{spanLabel}</span>
    <span className={styles.legendItem}><span className={styles.legendMarker} />{markerLabel}</span>
  </span>
);

export default DotRange;
